import {
  convertBase64ToBytes,
  convertBase64URLToBytes,
  convertBytesToBase64,
  convertBytesToBase64URL,
  convertBytesToHex,
  convertBytestoString,
  convertHexToBytes,
  convertStringToBytes,
} from '../utils/string.js';
import {
  GamoraFoundationGuard,
  GamoraFoundationGuardConstructorType,
} from '../foundation/guard/guard.js';
import { assert } from '../utils/assert.js';
import { GamoraErrorValidation } from '../errors/validation.error.js';

/**
 * Input type
 */
export type GamoraBase64GuardInput = string;

/**
 * GamoraBase64Guard
 * A typesafe and guarded Base64 encoded type
 * See (RFC 4648).
 *
 * @example "U3Vic3QuaWQgcm9ja3M="
 * @public
 */
export class GamoraBase64Guard extends GamoraFoundationGuard<GamoraBase64GuardInput> {
  protected _base64 = '';

  /**
   * Valid Base64 Regex
   */
  static RegExp =
    /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

  /**
   * Constructor
   * @param input The input
   */
  constructor(input: string) {
    super();
    this.base64 = input;
  }

  /**
   * Set Base64
   * @param base64 The base64
   */
  set base64(base64: string) {
    assert(
      GamoraBase64Guard.RegExp.test(base64),
      'Invalid base64 argument: invalid GamoraBase64Guard.',
      GamoraErrorValidation,
    );
    this._base64 = base64;
  }

  /**
   * Get GamoraBase64Guard
   */
  get base64() {
    return this._base64;
  }

  /**
   * Gets GamoraBase64Guard Length
   */
  get length() {
    return this._base64.length;
  }

  /**
   * Gets from JSON
   * @param input Input string
   */
  static override fromJSON<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    return new this(input);
  }

  /**
   * Gets from Base64
   */
  static override fromBase64<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    return new this(input);
  }

  /**
   * Gets from empty
   */
  static override Empty<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
  ) {
    return new this('');
  }

  /**
   * Tests if input string is valid GamoraBase64Guard
   * @param input The input string
   */
  static Test<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: string,
  ): boolean {
    return GamoraBase64Guard.RegExp.test(input);
  }

  /**
   * Gets from Base64URL
   */
  static override fromBase64URL<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const bytes = convertBase64URLToBytes(input);
    return GamoraBase64Guard.fromJSON(convertBytesToBase64(bytes));
  }

  /**
   * Gets from Base64URL
   */
  static override fromHex<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const bytes = convertHexToBytes(input);
    return GamoraBase64Guard.fromJSON(convertBytesToBase64(bytes));
  }

  /**
   * Gets from UTF8
   */
  static override fromUTF8<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const bytes = convertStringToBytes(input);
    return GamoraBase64Guard.fromJSON(convertBytesToBase64(bytes));
  }

  /**
   * Gets from PEM
   */
  static fromPEM<TClass extends GamoraBase64Guard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraBase64GuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const base64Content = input
      .replace(/-----BEGIN [^-]+-----/, '')
      .replace(/-----END [^-]+-----/, '')
      .replace(/\s+/g, '');
    return GamoraBase64Guard.fromBase64(base64Content);
  }

  /**
   * Gets UTF8
   */
  override toUTF8() {
    const bytes = convertBase64ToBytes(this.base64);
    return convertBytestoString(bytes);
  }

  /**
   * Gets base64
   */
  override toBase64URL() {
    const bytes = convertBase64ToBytes(this.base64);
    return convertBytesToBase64URL(bytes);
  }

  /**
   * Gets base64
   */
  override toHex() {
    const bytes = convertBase64ToBytes(this.base64);
    return convertBytesToHex(bytes);
  }

  /**
   * Checks if base64 is empty
   */
  isEmpty() {
    return this.base64 === '';
  }

  /**
   * Gets JSON
   */
  override toJSON() {
    return this.base64;
  }

  /**
   * Gets base64
   */
  override toBase64() {
    return this.base64;
  }

  /**
   * Gets base64
   */
  override toString() {
    return this.base64;
  }

  /**
   * Clone
   */
  override clone() {
    return new (this.constructor as GamoraFoundationGuardConstructorType<
      this,
      GamoraBase64GuardInput
    >)(this.toJSON());
  }
}
